// ============================================================
// ELC Shift — Uploads / Addon Actuals (weekly bulk) module
//
// Reads a 5-sheet xlsx (Demo / Batteries / Gift Wrap / Reusable
// Bag / Gift Cards), each sheet a wide-format matrix (Date down
// rows, stores across columns), merges into per-store-per-date
// rows with one field per category, and upserts to `addon_actuals`.
//
// Different shape from Daily Upload: this is BULK historical (one
// xlsx, many days, all 5 categories at once). Daily Upload handles
// the daily morning xlsx (single day, sales + addon together).
//
// Public surface (callable from admin.html inline handlers):
//   Uploads.addon.handleDrop(event)         — file dropzone drop
//   Uploads.addon.handleFile(file)          — programmatic file input
//   Uploads.addon.acceptSuggestion(...)     — "Did you mean...?" accept
//   Uploads.addon.dismissSuggestion(store)  — "Did you mean...?" skip
//   Uploads.addon.save()                    — upsert to DB
//   Uploads.addon.clearPreview()            — wipe preview UI
//   Uploads.addon.downloadTemplate()        — generate empty xlsx template
//
// Free globals (referenced as window.foo):
//   - XLSX                  (SheetJS)
//   - buildUploadNameMap    (admin.html helper)
//   - escapeHtml            (admin.html escape utility)
//   - ALL_STORES            (admin.html canonical store list)
//   - staffData             (admin.html staff list)
//   - getStoreType          (admin.html — returns 'virtual' for relief)
//   - getStoreUploadName    (admin.html — returns xlsx column name)
//   - DB                    (db.js DB facade)
//   - showToast             (admin.html toast helper)
// ============================================================

import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import {
  parseMatrixSheet,
  findStoreColumnSuggestions,
  renderStoreSuggestionCards,
} from './shared.js';

// Module state — fresh on every parsed file.
let parsedAddonActuals    = null;
let addonActualsParseState = null;

// Category → DB field mapping. Sheet names are matched case-insensitively
// against workbook sheet names.
const ADDON_SHEET_FIELDS = [
  { sheet: 'demo',          field: 'demo_units' },
  { sheet: 'batteries',     field: 'batteries' },
  { sheet: 'gift wrap',     field: 'gift_wrap' },
  { sheet: 'reusable bag',  field: 'reusable_bag' },
  { sheet: 'gift cards',    field: 'gift_cards' },
];

export function handleDrop(e) {
  e.preventDefault();
  if (e.currentTarget && e.currentTarget.style) {
    e.currentTarget.style.borderColor = 'var(--border)';
    e.currentTarget.style.background = 'var(--surface-2)';
  }
  if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
}

export function handleFile(file) {
  if (!file || !file.name.endsWith('.xlsx')) {
    showAddonActualsStatus('Please upload an .xlsx file', 'error'); return;
  }
  parsedAddonActuals = null;
  addonActualsParseState = null;
  showAddonActualsStatus('Reading ' + file.name + '…', 'info');
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') { ensureXlsx().then(function () { handleFile(file); }, function (e) { showAddonActualsStatus(e.message, 'error'); }); return; }
  const reader = new FileReader();
  reader.onload = function(e) {
    try {
      const wb = XLSX.read(e.target.result, {type:'array', cellDates:true});

      // Pre-extract row arrays per sheet so accept-suggestion can re-parse
      // without re-reading the file. Stores the per-sheet rows + the
      // shared store-name map + accepted/dismissed state.
      const dynamicMap = buildUploadNameMap();
      const storeMapUpper = {};
      Object.entries(dynamicMap).forEach(([k,v]) => { storeMapUpper[k.trim().toUpperCase()] = v; });

      const sheetByName = {};
      wb.SheetNames.forEach(n => { sheetByName[n.toLowerCase()] = n; });
      const sheetRows = {};   // {field: rows[]}
      ADDON_SHEET_FIELDS.forEach(({sheet, field}) => {
        const realName = sheetByName[sheet];
        if (!realName) return;
        sheetRows[field] = XLSX.utils.sheet_to_json(wb.Sheets[realName], {header:1, raw:false, dateNF:'yyyy-mm-dd'});
      });

      addonActualsParseState = {
        sheetRows,
        storeMapUpper,
        accepted: [],          // [{header, appName}] — applied to every sheet
        dismissed: new Set(),  // store names user clicked "Skip" on
        filename: file.name,
      };

      rebuildFromState();
    } catch(err) {
      showAddonActualsStatus('Error reading file: ' + err.message, 'error');
      console.error('[addon-actuals]', err);
    }
  };
  reader.readAsArrayBuffer(file);
}

// v79.4: re-runs the multi-sheet parse using current state. Called on
// initial parse, after [Use this] click, and after [Skip] click.
function rebuildFromState() {
  if (!addonActualsParseState) return;
  const { sheetRows, storeMapUpper, accepted, dismissed } = addonActualsParseState;

  // Aggregate into {store: {date: {field: count}}} across all sheets.
  // Also dedupe unmatched headers across sheets — they share the same
  // store columns, so a header missing from one is missing from all.
  const merged = {};
  const sheetsFound = [];
  const sheetsMissing = [];
  const unmatchedHeadersByText = new Map();

  ADDON_SHEET_FIELDS.forEach(({sheet, field}) => {
    if (!sheetRows[field]) { sheetsMissing.push(sheet); return; }
    sheetsFound.push(sheet);

    // Build extras for THIS sheet: each accepted entry maps a header text
    // to an appName. We need the column index for each sheet (could vary
    // in theory if sheets diverge, though they shouldn't in the master file).
    const headerRow = sheetRows[field][findHeaderRow(sheetRows[field])] || [];
    const extrasForThisSheet = accepted.map(e => {
      const idx = headerRow.findIndex(h => String(h || '').trim() === e.header);
      return idx >= 0 ? { idx, appName: e.appName } : null;
    }).filter(Boolean);

    const parsed = parseMatrixSheet(sheetRows[field], storeMapUpper, 'Date', extrasForThisSheet);
    if (!parsed.ok) { sheetsMissing.push(sheet + ' (' + parsed.error + ')'); return; }

    Object.entries(parsed.byStoreDate).forEach(([store, dates]) => {
      if (!merged[store]) merged[store] = {};
      Object.entries(dates).forEach(([date, value]) => {
        if (!merged[store][date]) merged[store][date] = {};
        merged[store][date][field] = Math.round(Number(value));
      });
    });

    // Collect unmatched headers — keyed by text so duplicates across sheets
    // don't appear multiple times in the suggestion UI.
    (parsed.unmatchedHeaders || []).forEach(h => {
      if (!unmatchedHeadersByText.has(h.header)) unmatchedHeadersByText.set(h.header, h);
    });
  });

  if (!sheetsFound.length) {
    showAddonActualsStatus(
      'No recognised category sheets found. Expected one or more of: ' +
        ADDON_SHEET_FIELDS.map(s => '"' + s.sheet + '"').join(', ') + '.',
      'error'
    );
    return;
  }

  const flat = [];
  Object.entries(merged).forEach(([store, dates]) => {
    Object.entries(dates).forEach(([date, fields]) => {
      flat.push(Object.assign({ store, date }, fields));
    });
  });
  parsedAddonActuals = flat;

  const storeCount = new Set(flat.map(r => r.store)).size;
  const dateCount  = new Set(flat.map(r => r.date)).size;
  const expectedStores = Array.isArray(ALL_STORES)
    ? ALL_STORES.filter(s => s && s !== 'Retail Relief Managers')
    : [];
  const matchedSet = new Set(flat.map(r => r.store));
  const missingStores = expectedStores.filter(s => !matchedSet.has(s));
  const unmatchedHeaders = Array.from(unmatchedHeadersByText.values());
  const suggestions = findStoreColumnSuggestions(unmatchedHeaders, missingStores, dismissed);

  let statusMsg = '✅ Parsed ' + flat.length + ' rows: ' + storeCount + ' stores × ' + dateCount + ' dates. Sheets used: ' + sheetsFound.join(', ') + '.';
  if (sheetsMissing.length) statusMsg += ' Skipped: ' + sheetsMissing.join(', ') + '.';
  if (suggestions.length) statusMsg += ' See suggestions below.';
  showAddonActualsStatus(statusMsg, missingStores.length === 0 ? 'success' : 'info');

  const previewCard = document.getElementById('addonActualsPreviewCard');
  if (previewCard) previewCard.style.display = 'block';
  const previewEl = document.getElementById('addonActualsPreview');
  if (!previewEl) return;

  const sample = flat.slice(0, 8);
  let html = '<div style="font-size:12px;color:var(--sub);margin-bottom:8px">' + flat.length + ' total rows. Sample (first 8):</div>';

  // Suggestion cards — namespaced for inline onclick resolution.
  html += renderStoreSuggestionCards(suggestions, 'Uploads.addon.acceptSuggestion', 'Uploads.addon.dismissSuggestion');

  // Stores still missing AND with no suggestion
  const suggestedStores = new Set(suggestions.map(s => s.store));
  const orphanMissing = missingStores.filter(s => !suggestedStores.has(s));
  if (orphanMissing.length > 0 && expectedStores.length > 0) {
    html += '<div style="background:var(--adm-warn-pale);border:1px solid var(--adm-warn-line);color:var(--warn-deep);padding:8px 10px;border-radius:6px;margin-bottom:8px;font-size:12px"><strong>⚠️ ' + orphanMissing.length + ' store' + (orphanMissing.length === 1 ? '' : 's') + ' missing — no close match in file:</strong><br>' + orphanMissing.map(s => '• ' + escapeHtml(s)).join('<br>') + '</div>';
  }

  html += '<table style="width:100%;font-size:12px;border-collapse:collapse">';
  html += '<thead><tr><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--border)">Store</th><th style="text-align:start;padding:4px 8px;border-bottom:1px solid var(--border)">Date</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Demo</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Bat</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Wrap</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Bag</th><th style="text-align:end;padding:4px 8px;border-bottom:1px solid var(--border)">Cards</th></tr></thead><tbody>';
  sample.forEach(r => {
    html += '<tr>';
    html += '<td style="padding:4px 8px">' + escapeHtml(r.store) + '</td>';
    html += '<td style="padding:4px 8px">' + escapeHtml(r.date) + '</td>';
    ['demo_units','batteries','gift_wrap','reusable_bag','gift_cards'].forEach(f => {
      const v = r[f];
      html += '<td style="text-align:end;padding:4px 8px;font-variant-numeric:tabular-nums">' + (v != null ? v : '—') + '</td>';
    });
    html += '</tr>';
  });
  html += '</tbody></table>';
  previewEl.innerHTML = html;
}

// Helper: locate header row (the row containing "Date") within a sheet's
// row array. Mirrors the search logic in parseMatrixSheet.
function findHeaderRow(rows) {
  for (let i = 0; i < Math.min(5, rows.length); i++) {
    const r = rows[i];
    if (r && r.some(c => String(c || '').trim().toUpperCase() === 'DATE')) return i;
  }
  return 0;
}

export function acceptSuggestion(store, colIdx, headerLabel) {
  if (!addonActualsParseState) return;
  // Track by HEADER TEXT (not idx) — index may vary across sheets.
  addonActualsParseState.accepted = addonActualsParseState.accepted.filter(e => e.appName !== store);
  addonActualsParseState.accepted.push({ header: headerLabel, appName: store });
  rebuildFromState();
  showToast('Mapped "' + headerLabel + '" → ' + store, 'success');
}

export function dismissSuggestion(store) {
  if (!addonActualsParseState) return;
  addonActualsParseState.dismissed.add(store);
  rebuildFromState();
}

export async function save() {
  if (!parsedAddonActuals || !parsedAddonActuals.length) {
    showAddonActualsStatus('No parsed data to save', 'error'); return;
  }
  showAddonActualsStatus('⏳ Uploading ' + parsedAddonActuals.length + ' rows…', 'info');
  try {
    const result = await DB.addon_actuals.upload(parsedAddonActuals);
    showAddonActualsStatus('✅ Saved ' + (result.inserted || parsedAddonActuals.length) + ' rows. KPI tab in staff app will show actuals.', 'success');
    showToast('Addon actuals uploaded — ' + (result.inserted || parsedAddonActuals.length) + ' rows', 'success');
    parsedAddonActuals = null;
    addonActualsParseState = null;
    const previewCard = document.getElementById('addonActualsPreviewCard');
    if (previewCard) previewCard.style.display = 'none';
  } catch (e) {
    showAddonActualsStatus('❌ Upload failed: ' + e.message, 'error');
    showToast('Addon actuals upload failed — see console', 'error');
  }
}

export function clearPreview() {
  parsedAddonActuals = null;
  addonActualsParseState = null;
  const previewCard = document.getElementById('addonActualsPreviewCard');
  if (previewCard) previewCard.style.display = 'none';
  const fileInput = document.getElementById('addonActualsFileInput');
  if (fileInput) fileInput.value = '';
  showAddonActualsStatus('Cancelled.', 'info');
}

function showAddonActualsStatus(msg, type) {
  const el = document.getElementById('addonActualsStatus');
  const inner = document.getElementById('addonActualsStatusInner');
  if (!el || !inner) return;
  el.style.display = 'block';
  const colors = {
    success: {bg:'var(--adm-ok-pale)',color:'var(--ok-deep)',border:'var(--adm-ok-line)'},
    error:   {bg:'var(--adm-danger-pale)',color:'var(--danger-deep)',border:'var(--adm-danger-line)'},
    info:    {bg:'var(--info-bg)',color:'var(--adm-sky-text)',border:'var(--adm-info-line)'},
  };
  const c = colors[type] || colors.info;
  inner.style.cssText = `background:${c.bg};color:${c.color};border:1px solid ${c.border};padding:12px 14px;border-radius:10px;font-size:13px`;
  inner.textContent = msg;
}

// ── Add-on actuals template (v79.4) ─────────────────────
// Generates a 5-sheet xlsx with the wide-format matrix per category.
// Matches the shape rebuildFromState expects: each sheet has
// Day | Date | Month | Week | + N store columns. Cells are blank for
// the user to fill in their daily counts.
export function downloadTemplate() {
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') {
    ensureXlsx().then(function () { downloadTemplate(); }, function (e) { showToast(e.message, 'error'); }); return;
  }

  // Same store-list logic as the budget/sales templates so all three
  // produce identical column headers.
  const storeSet = new Set(ALL_STORES || []);
  (staffData || []).forEach(s => { if (s.store) storeSet.add(s.store); });
  const appStores = [...storeSet]
    .filter(s => s !== 'Retail Relief Managers' && getStoreType(s) !== 'virtual')
    .sort();
  const storeXlsxNames = appStores.map(s => getStoreUploadName(s));

  const dayNames   = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
  const monthNames = ['January','February','March','April','May','June','July',
                      'August','September','October','November','December'];
  const year = new Date().getFullYear();

  function isoWeek(dt) {
    const tmp = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    return Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
  }

  // Build the row array for one category sheet. Same shape used 5×.
  const metaCols = ['Day', 'Date', 'Month', 'Week'];
  function buildSheetRows(categoryLabel) {
    const headerRow = [...metaCols, ...storeXlsxNames];
    // Match parseMatrixSheet's "header row in top 5" tolerance: row 0 is
    // a placeholder/title, row 1 is the real headers.
    const placeholderRow = Array(headerRow.length).fill('');
    placeholderRow[0] = categoryLabel + ' actuals — fill counts below';
    const rows = [placeholderRow, headerRow];
    let d = new Date(year, 0, 1);
    const endD = new Date(year, 11, 31);
    while (d <= endD) {
      const dateStr = d.getFullYear() + '-' +
                      String(d.getMonth()+1).padStart(2,'0') + '-' +
                      String(d.getDate()).padStart(2,'0');
      const dayName = dayNames[d.getDay() === 0 ? 6 : d.getDay()-1];
      const monthName = monthNames[d.getMonth()];
      rows.push([dayName, dateStr, monthName, isoWeek(d), ...storeXlsxNames.map(() => '')]);
      d.setDate(d.getDate() + 1);
    }
    return rows;
  }

  const wb = XLSX.utils.book_new();
  // Sheet names MUST match what rebuildFromState expects (case-
  // insensitive): Demo, Batteries, Gift Wrap, Reusable Bag, Gift Cards.
  const sheets = [
    { name: 'Demo',         label: 'Demo Units' },
    { name: 'Batteries',    label: 'Batteries' },
    { name: 'Gift Wrap',    label: 'Gift Wrap' },
    { name: 'Reusable Bag', label: 'Reusable Bag' },
    { name: 'Gift Cards',   label: 'Gift Cards' },
  ];
  const colWidths = [
    {wch:11},{wch:12},{wch:10},{wch:6},
    ...storeXlsxNames.map(() => ({wch:20}))
  ];
  sheets.forEach(({name, label}) => {
    const ws = XLSX.utils.aoa_to_sheet(buildSheetRows(label));
    ws['!cols'] = colWidths;
    ws['!freeze'] = { xSplit: 4, ySplit: 2 };
    XLSX.utils.book_append_sheet(wb, ws, name);
  });

  // README sheet so the file explains itself when shared
  const readmeRows = [
    ['Shift — Add-on KPI Actuals Template (' + year + ')'],
    [''],
    ['Five sheets: Demo, Batteries, Gift Wrap, Reusable Bag, Gift Cards.'],
    ['Each sheet is a wide matrix: rows = days, columns = stores.'],
    ['Cells are integer counts (units sold per store per day).'],
    ['Leave a cell blank if you don\'t have the count yet — blank/zero cells'],
    ['are skipped on upload (treated as "not yet recorded").'],
    [''],
    ['Stores: ' + appStores.length + ' canonical stores. Re-name a column'],
    ['header in the Stores tab if your spreadsheet uses different store names.'],
    ['On upload, unrecognised columns surface as "Did you mean…?" suggestions.'],
    [''],
    ['Upload via: Admin → Targets → 🎁 Add-on KPI → Upload daily actuals.'],
  ];
  const readmeWs = XLSX.utils.aoa_to_sheet(readmeRows);
  readmeWs['!cols'] = [{wch:80}];
  XLSX.utils.book_append_sheet(wb, readmeWs, 'README');

  XLSX.writeFile(wb, 'ELC_Addon_Actuals_Template_' + year + '.xlsx');
  showToast('Template downloaded — 5 sheets with ' + appStores.length + ' stores', 'success');
}
